import BaseSettingsPage from "@settings/base/BaseSettingsPage";
import LawSelectionCard from "./LawSelectionCard";
import { useDocumentsList, Document } from "@documents/hooks/useDocumentsList";
import { CircularProgress, Box, Typography, Accordion, AccordionSummary, AccordionDetails, Alert, IconButton, Checkbox, FormControlLabel } from "@mui/material";
import { getDocumentCategory } from "@/utils/documentUtils";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDocumentSelection } from '@documents/hooks/useDocumentSelection';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import SettingsCard from "../../base/SettingsCard";
import { useQuery, useMutation } from "@apollo/client";
import { UPDATE_USER_PROFILE } from "@company/graphql/mutations/updateUserProfile";
import { FETCH_USER_PROFILE } from "@company/graphql/queries/fetchUserProfile";
import { Mutation, MutationUpdateUserArgs, Query, UserChatSetting_Language, UserChatSetting_ResponseLength, UserChatSetting_ResponseStyle, UserChatSetting_Verbosity, UserChatSettings } from "@/app/graphql/codegen";
import SubscriptionGate from "@ui/SubscriptionGate";
import { useSubscriptionAccessContext } from "../../../../contexts/SubscriptionAccessContext";

const LawSelectionPage = () => {
    // 1) Check subscription status first
    const { hasAccess, loading: accessLoading } = useSubscriptionAccessContext();

    // 2) Fetch documents only if user has subscription access
    const { documents, loading: listLoading, error } = useDocumentsList(!hasAccess);
    const { bulkToggleDocumentSelections, loading: saveLoading } = useDocumentSelection();

    // Nachverfolgung des Lade- und Renderzustands in einer Variablen
    // 0=Daten werden abgerufen, 1=Kategorien werden geladen, 2=vollständig
    const [renderState, setRenderState] = useState(0);

    // Lokaler Auswahlzustand
    const [selectionState, setSelectionState] = useState<Record<string, boolean>>({});

    // Chat settings (Dokumentansicht)
    const defaultChatSettings: UserChatSettings = {
        language: UserChatSetting_Language.De,
        verbosity: UserChatSetting_Verbosity.Fluent,
        responseLength: UserChatSetting_ResponseLength.Medium,
        responseStyle: UserChatSetting_ResponseStyle.Scientific,
        showAmendments: false,
        showOriginals: false,
        showSummaries: false,
    };

    const { data: profileData, loading: profileLoading } = useQuery<Pick<Query, "userProfile">>(FETCH_USER_PROFILE);
    const [updateProfile, { loading: updateLoading }] = useMutation<Pick<Mutation, "updateUser">, MutationUpdateUserArgs>(UPDATE_USER_PROFILE);

    const [chatSettings, setChatSettings] = useState<UserChatSettings>(defaultChatSettings);
    const [chatHasChanges, setChatHasChanges] = useState(false);

    // Initialisierung des lokalen Zustands aus abgerufenen Dokumenten
    useEffect(() => {
        const initialState: Record<string, boolean> = {};
        documents.forEach(doc => {
            initialState[doc.id] = doc.isActiveForCompany !== false;
        });
        setSelectionState(initialState);
    }, [documents]);

    // Originalzustand für Vergleich speichern
    const originalState = useMemo(() => {
        const orig: Record<string, boolean> = {};
        documents.forEach(doc => {
            orig[doc.id] = doc.isActiveForCompany !== false;
        });
        return orig;
    }, [documents]);

    // Überprüfung auf ungespeicherte Änderungen für Dokumentauswahl
    const docHasChanges = useMemo(
        () => Object.keys(selectionState).some(id => selectionState[id] !== originalState[id]),
        [selectionState, originalState]
    );

    const overallHasChanges = docHasChanges || chatHasChanges;

    // Handler
    const handleDocumentToggle = useCallback((documentId: string, newState: boolean) => {
        setSelectionState(prev => ({ ...prev, [documentId]: newState }));
    }, []);

    const handleCategoryToggle = useCallback((docs: Document[], newState: boolean) => {
        setSelectionState(prev => {
            const next = { ...prev };
            docs.forEach(d => { next[d.id] = newState; });
            return next;
        });
    }, []);

    // Speicher-Handler: Stapelverarbeitung von API-Aufrufen für geänderte Dokumente
    const onSave = useCallback(async () => {
        const toActivate: string[] = [];
        const toDeactivate: string[] = [];

        documents.forEach(doc => {
            const orig = originalState[doc.id];
            const current = selectionState[doc.id];
            if (current !== orig) {
                if (current) toActivate.push(doc.id);
                else toDeactivate.push(doc.id);
            }
        });

        let success = true;
        if (toActivate.length) {
            const ok = await bulkToggleDocumentSelections(toActivate, true);
            success = success && ok;
        }
        if (toDeactivate.length) {
            const ok = await bulkToggleDocumentSelections(toDeactivate, false);
            success = success && ok;
        }

        // Save chat settings if changed
        if (chatHasChanges && profileData?.userProfile?.id) {
            try {
                await updateProfile({
                    variables: {
                        id: profileData.userProfile.id,
                        chatSettings: chatSettings,
                    },
                    refetchQueries: [{ query: FETCH_USER_PROFILE }],
                });
                setChatHasChanges(false);
            } catch (error) {
                console.error("Failed to save chat settings:", error);
                success = false;
            }
        }

        return success;
    }, [documents, originalState, selectionState, bulkToggleDocumentSelections, chatHasChanges, chatSettings, profileData, updateProfile]);

    // Gruppierung verzögerter (und ausgewählter) Dokumente nach Kategorie nach dem Übergang
    const documentsByCategory = useMemo(() => {
        if (renderState === 0) return {}; // Nicht während der anfänglichen Datenabrufung verarbeiten
        
        return documents.reduce((acc: Record<string, Document[]>, document) => {
            const category = getDocumentCategory(document);
            if (!acc[category]) acc[category] = [];
            acc[category].push(document);
            return acc;
        }, {} as Record<string, Document[]>);
    }, [documents, renderState]);

    // Zählung ausgewählter Dokumente (lokal)
    const selectedCount = Object.values(selectionState).filter(v => v).length;
    const totalCount = documents.length;

    useEffect(() => {
        if (listLoading) {
            setRenderState(0); // Daten werden geladen
        }
        else if (documents.length > 0) {
            // Rendering in zwei Phasen mit sichtbarem Ladevorgang dazwischen planen
            requestAnimationFrame(() => {
                setRenderState(1); // Kategorien werden geladen
                
                // Vollständiges Rendering des Inhalts nach kurzer Verzögerung planen
                setTimeout(() => {
                    requestAnimationFrame(() => {
                        setRenderState(2); // Rendering abgeschlossen
                    });
                }, 100);
            });
        }
    }, [listLoading, documents]);

    // initialize chat settings from query result
    useEffect(() => {
        if (profileData?.userProfile?.chatSettings) {
            setChatSettings({
                ...defaultChatSettings,
                ...profileData.userProfile.chatSettings,
            } as UserChatSettings);
        }
    }, [profileData]);

    const handleChatSettingChange = (key: keyof UserChatSettings) => (value: boolean) => {
        setChatSettings((prev) => ({ ...prev, [key]: value }));
        setChatHasChanges(true);
    };

    // Titel entsprechend dem Ladezustand anpassen
    const getTitle = () => {
        if (renderState < 2) {
            return "Gesetzesübersicht — wird geladen...";
        }
        return `Gesetzesübersicht — ${selectedCount}/${totalCount}`;
    };

    // Fehler zuerst anzeigen (nachdem Zugriff bestätigt)
    if (error) {
        return (
            <BaseSettingsPage title="Gesetzesübersicht">
                Fehler beim Laden der Dokumente
            </BaseSettingsPage>
        );
    }

    return (
        <SubscriptionGate fullHeight>
        <BaseSettingsPage
            title={getTitle()}
            onSave={onSave}
            saveDisabled={!overallHasChanges || saveLoading || updateLoading}
            hasChanges={overallHasChanges}
            useOuterScroll
        >
            <Box mb={3}>
                <Alert severity="info" sx={{ mb: 2 }}>
                    Hier kannst Du Gesetze für Dein Unternehmen an- und abwählen. Die Auswahl wird erst nach Klick auf "Speichern" übertragen.
                </Alert>

                <SettingsCard title="Dokumentansicht">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={chatSettings.showAmendments || false}
                                onChange={(e) => handleChatSettingChange("showAmendments")(e.target.checked)}
                                disabled={profileLoading}
                            />
                        }
                        label="Änderungen anzeigen"
                        sx={{ width: '100%', mb: 1 }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={chatSettings.showOriginals || false}
                                onChange={(e) => handleChatSettingChange("showOriginals")(e.target.checked)}
                                disabled={profileLoading}
                            />
                        }
                        label="Originale anzeigen"
                        sx={{ width: '100%', mb: 1 }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={chatSettings.showSummaries || false}
                                onChange={(e) => handleChatSettingChange("showSummaries")(e.target.checked)}
                                disabled={profileLoading}
                            />
                        }
                        label="Zusammenfassungen anzeigen"
                        sx={{ width: '100%', mb: 1 }}
                    />
                </SettingsCard>
            </Box>

            <Box sx={{ p: 1, pr: 3 }}>
                {renderState < 2 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" minHeight={300}>
                        <CircularProgress size={40} />
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            {renderState === 0 ? "Dokumente werden geladen..." : "Gesetze werden verarbeitet..."}
                        </Typography>
                    </Box>
                ) : Object.keys(documentsByCategory).length === 0 ? (
                    <Box p={3} textAlign="center">
                        <Typography variant="body1">Keine Dokumente gefunden</Typography>
                    </Box>
                ) : (
                    Object.entries(documentsByCategory)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([category, docs]) => {
                            const sorted = docs.sort((a, b) => (a.reference || "").localeCompare(b.reference || ""));
                            const allSelected = sorted.every(d => selectionState[d.id]);
                            const categoryTotal = sorted.length;
                            const categorySelected = sorted.filter(d => selectionState[d.id]).length;
                            return (
                                <Accordion key={category} sx={{ mb: 2 }} TransitionProps={{ unmountOnExit: true }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                            <Typography variant="h6">{category}</Typography>
                                            <Box display="flex" alignItems="center">
                                                <Typography variant="body2" color="text.secondary">
                                                    {categorySelected}/{categoryTotal}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); handleCategoryToggle(sorted, !allSelected); }}
                                                    sx={{ ml: 1 }}
                                                >
                                                    {allSelected ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                                                </IconButton>
                                                <Typography variant="caption" sx={{ ml: 0.5 }}>
                                                    {allSelected ? 'Alle abwählen' : 'Alle auswählen'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ p: 1 }}>
                                        {renderState === 2 && (
                                            <LawSelectionCard
                                                documents={sorted}
                                                selectionState={selectionState}
                                                onToggle={handleDocumentToggle}
                                                showOriginals={chatSettings.showOriginals === true}
                                                showAmendments={chatSettings.showAmendments === true}
                                            />
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })
                )}
            </Box>
        </BaseSettingsPage>
        </SubscriptionGate>
    );
};

export default LawSelectionPage;
